/**
 * The commander's table (L1, read-only; the coordinator's row after decision 133): what the diorama view draws, as data.
 * One marker per squad of the viewer's team — where its living members stand (their centre), how many stand, its order in
 * words and the point that order is about (a capture point, a unit it escorts, its exit, a fire mission's target) — and
 * the enemy only as the team knows it (core/picture.ts teamPicture: what the team's eyes shared, the sightings, the
 * marks), never the truth. With no squads in the state (an uncommanded skirmish) the team is shown in threes, as the
 * squads would be made. X23's grammar: the same markers at every zoom. Pure.
 */
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { seatRespawn } from './respawnPick';
import { squadIntent, type SquadIntent } from './squadIntent';
import { vehiclesBarred } from './vehicleUse';
import { drives, structureJob, cargoOnly } from './body';
import { rideArrived } from './ride';
import { reserveView, type ReserveView } from './reserves';
import type { GameState, Play, PlayerState, SquadOrder, StructureState, Vec3, VehicleKind, UnitRole } from '../types';
import { squadOrderFor, squadOrderSourceFor, squadTaskFor, squadsForTeam } from './squads';
import { leftBehind } from './frontLine';
import { orderPlace } from './orderForms';
import { teamPicture, type Belief } from './picture';
import { mountedStructure } from './seats';
import { frontPoint, liveSectors, sectorEdge } from './capturePoints';
import { webLinks } from './web';
import { dacos } from './dmath';
import { SECTOR_MAX_SIDE } from '../constants';

/**
 * Lane E's order glyphs (68.1, DIRECTION §0.6: the radial's verbs and the strip), one per verb, shared by every place
 * an order is drawn — the overview, the L0 friendly markers (68.7), the strip (68.3).
 */
export const ORDER_GLYPHS = { go: '➜', hold: '◼', follow: '⟲', fire: '✕', formation: '⋮', tactics: '⤳', withdraw: '⇤', mark: '◎', build: '⚒' } as const;

/** The glyph for an order kind (today's kinds mapped onto the verbs; free = fire at will). */
export function orderGlyph(kind: SquadOrder['kind'] | null): string {
  switch (kind) {
    case 'take': case 'move': case 'ride': return ORDER_GLYPHS.go;
    case 'engage': case 'demolish': return ORDER_GLYPHS.fire;
    case 'crew': return ORDER_GLYPHS.hold;
    case 'hold': return ORDER_GLYPHS.hold;
    case 'escort': return ORDER_GLYPHS.follow;
    case 'bound': case 'flank': return ORDER_GLYPHS.tactics;
    case 'withdraw': return ORDER_GLYPHS.withdraw;
    case 'formation': return ORDER_GLYPHS.formation;
    case 'fireMission': case 'mark': case 'callIn': return ORDER_GLYPHS.mark;
    case 'build': return ORDER_GLYPHS.build;   // 69.2d
    default: return ORDER_GLYPHS.fire;
  }
}

export interface MemberLine {
  id: number; name: string; alive: boolean; health: number; doing: string; status: FriendlyStatus; planetIndex: number; dir: Vec3;
  /** 68.20: in a hull or a seat (no dot of its own: the body's mark stands for it). */
  mounted: boolean;
  /** 68.5: its own order, when it was given one apart from its squad's (a direct order): the glyph and the words. */
  own?: { glyph: string; text: string };
  /** 201 (8): its role (PlayerState.role), for the markers' badge. */
  role?: UnitRole;
}

export interface SquadMarker {
  id: number;
  label: string;
  team: number;
  planetIndex: number;
  /** The centre of the living members (a unit direction on their planet), or null when none stands — 68.36: a split squad's, its largest cluster's (never ground nobody holds). */
  centre: Vec3 | null;
  /** 68.36 (the owner: "squad can be separated and the actual block indicator is sort of wrong"): a split squad's other clusters — where its members stand apart (more than SQUAD_CLUSTER_PEOPLE from the rest), and how many each; absent when it is together. */
  clusters?: Array<{ dir: Vec3; n: number }>;
  alive: number;
  total: number;
  /** The order in a few words, and its glyph (lane E's set). */
  order: string;
  glyph: string;
  /** Where the order points, if anywhere. */
  target: { planetIndex: number; dir: Vec3 } | null;
  members: MemberLine[];
  /** 68.7: what the squad is doing (its most pressing member's state). */
  status: FriendlyStatus;
  /** 68.18: under a ride order, where the ride stands — boarding (some still on foot), en route (all aboard), arrived. */
  ride?: RidePhase;
  /** 68.30: the team's transport every living member rides, when they all ride one — the squad is drawn as a tab on its
   *  plate, not as a block of its own. A squad partly aboard keeps its block, at the centre of its members on foot. */
  aboard?: number;
  /** X40: what the squad is doing now (core/squadIntent.ts), and how many seconds since it began — the tag beside its block (lane E's look). */
  intent?: { intent: SquadIntent; age?: number };
  /** 69.5f (the owner's ruling: flag, not release): your order holds it at a place behind the front while the front is fought over — re-order it (core/frontLine.ts leftBehind). The card line says so. */
  leftBehind?: true;
}

export type RidePhase = 'boarding' | 'en route' | 'arrived';

/**
 * 68.18: a transport on the commander's table — the team's and nobody's where they stand, with their seats filled and
 * free; the enemy's only as the team knows them (a known enemy unit riding it, at the picture's place for that unit).
 */
export interface TransportMarker {
  id: number; team: number | null; enemy: boolean; planetIndex: number; dir: Vec3; seats: { filled: number; total: number } | null;
  /** 63.1s (the owner: "technicals don't have icons, for commander overview, should use the same system as transports (and
   *  motorcycles now that I think of it)"): every vehicle kind gets a plate — its kind for the glyph; `rides` when it is a
   *  transport a squad can be told to ride (the click's ride order), never the technical, the bike or the supply truck. */
  kind: VehicleKind; rides: boolean;
  /** 68.30: the team's squads with every living member aboard (by id, in the table's order; the lead the one with most
   *  aboard, the first on a tie) and the troops aboard (the seats filled) — the combined plate. Absent with none. */
  aboard?: { squads: number[]; lead: number; count: number };
}

/** 68.30: the transport every one of `alive` rides, when they all ride the same standing one; else null. */
function aboardOne(state: GameState, alive: readonly PlayerState[]): number | null {
  if (!alive.length) return null;
  const id = alive[0].mount?.bodyId;
  if (id === undefined || alive.some(p => p.mount?.bodyId !== id)) return null;
  const st = state.structures?.find(s => s.id === id);
  return st && isPlated(st) ? id : null;
}

/** 68.30: each of the team's transports learns the squads wholly aboard it, its lead, and the troops aboard. */
function withAboard(transports: TransportMarker[], squads: SquadMarker[], state: GameState): void {
  for (const tr of transports) {
    if (tr.enemy) continue;
    const on = squads.filter(m => m.aboard === tr.id);
    if (!on.length) continue;
    const riders = (m: SquadMarker) => m.members.filter(u => u.alive && state.players.find(p => p.id === u.id)?.mount?.bodyId === tr.id).length;
    const lead = on.reduce((a, b) => riders(b) > riders(a) ? b : a);
    tr.aboard = { squads: on.map(m => m.id), lead: lead.id, count: tr.seats?.filled ?? on.reduce((n, m) => n + riders(m), 0) };
  }
  // a squad aboard a hull no plate of ours carries (the enemy's) keeps its block
  const carried = new Set(transports.flatMap(t => t.aboard?.squads ?? []));
  for (const m of squads) if (m.aboard !== undefined && !carried.has(m.id)) delete m.aboard;
}

/** 63.1s: the vehicle kinds the table plates (the transport, the technical, the supply truck, the bike). */
const PLATED: ReadonlySet<string> = new Set<VehicleKind>(['wheeled', 'technical', 'supplyTruck', 'motorcycle']);
/** 63.1s: a standing vehicle of a kind the table plates. */
export function isPlated(st: StructureState): boolean { return st.health > 0 && drives(st) && PLATED.has(st.type); }

/** A vehicle that carries people (a transport), standing. */
export function isTransport(st: StructureState): boolean { return st.health > 0 && drives(st) && (structureJob(st).carry ?? 0) > 0 && !cargoOnly(st); }   // (69.4 step 5: never the supply truck)

export function transportMarkers(state: GameState, team: number, picture: readonly Belief[]): TransportMarker[] {
  const teamOf = new Map(state.players.map(p => [p.id, p.teamIndex] as const));
  const out: TransportMarker[] = [];
  for (const st of state.structures ?? []) {
    if (!isPlated(st)) continue;
    const owner = st.ownerId >= 0 ? teamOf.get(st.ownerId) ?? null : null;
    if (owner !== null && owner !== team) continue;
    const seats = st.seats ?? [];
    out.push({ id: st.id, team: owner, enemy: false, planetIndex: st.planetIndex, dir: st.surfaceDir, seats: { filled: seats.filter(s => s.occupant).length, total: seats.length }, kind: st.type as VehicleKind, rides: isTransport(st) });
  }
  // the enemy's: where the team last placed a unit that rides one (never the truth)
  const seen = new Set<number>();
  for (const b of picture) {
    if (b.unitId === undefined) continue;
    const u = state.players.find(p => p.id === b.unitId);
    const st = u?.mount ? state.structures.find(s => s.id === u.mount!.bodyId) : undefined;
    if (!st || !isPlated(st) || seen.has(st.id)) continue;
    const owner = st.ownerId >= 0 ? teamOf.get(st.ownerId) ?? null : null;
    if (owner === team) continue;
    seen.add(st.id);
    out.push({ id: st.id, team: owner, enemy: true, planetIndex: b.planetIndex, dir: b.surfaceDir, seats: null, kind: st.type as VehicleKind, rides: false });
  }
  return out;
}

/** 68.18: the ride phase of a squad's ride order, from its living members and the transport. */
export function ridePhase(state: GameState, order: Extract<SquadOrder, { kind: 'ride' }>, members: readonly PlayerState[]): RidePhase {
  if (rideArrived(state, order)) return 'arrived';
  const alive = members.filter(p => p.alive);
  return alive.length && alive.every(p => p.mount?.bodyId === order.structureId) ? 'en route' : 'boarding';
}

/** 68.4: `reserve` — the team's reserve (X19 on): still to come, waiting to come in, held, where it lands; null without reserves. */
export interface CommandTable { squads: SquadMarker[]; enemy: Belief[]; pictureOn: boolean; reserve: ReserveView | null; /** 68.18 */ transports: TransportMarker[]; /** 69.6d */ front: FrontView | null; /** 69.2d: the web's links (none without relays) */ links: ReturnType<typeof webLinks>; /** 68.22 */ plays: PlayView[]; /** 68.25: the side kept on foot (a `vehicles` input) */ onFoot: boolean; /** 69.6h: the seat's respawn pick, its place and whether it is honoured now */ respawn: ReturnType<typeof seatRespawn> }

/**
 * 68.7: what a friendly is doing, read from the match state and its record (never by the renderer): down; hurt
 * (bandaging, or under half health); in contact (fired or was hit in the last few seconds); crewing a gun; scouting
 * (a drone of its up); withdrawing; holding; moving with its order; free.
 */
export type FriendlyStatus = 'down' | 'hurt' | 'in contact' | 'crewing' | 'scouting' | 'withdrawing' | 'supporting' | 'holding' | 'moving' | 'free';

/** Seconds after a shot or a hit that a unit still counts as in contact. */
export const CONTACT_SECONDS = 4;

export function friendlyStatus(state: GameState, unit: PlayerState, order: SquadOrder | null, lastShot: number | undefined, lastHurt: number | undefined, tick: number, dt: number): FriendlyStatus {
  if (!unit.alive) return 'down';
  if (unit.healLeft !== undefined || unit.health < 50) return 'hurt';
  const window = CONTACT_SECONDS / dt;
  if ((lastShot !== undefined && tick - lastShot <= window) || (lastHurt !== undefined && tick - lastHurt <= window)) return 'in contact';
  if (mountedStructure(unit, state)) return 'crewing';
  if (state.drones?.some(d => d.operatorId === unit.id)) return 'scouting';
  if (!order || order.kind === 'free') return 'free';
  if (order.kind === 'withdraw') return 'withdrawing';
  if (squadTaskFor(state, unit.id, tick)?.role === 'support') return 'supporting';   // 68.22: a play's base of fire (lane E's marker state)
  if (order.kind === 'hold') return 'holding';
  if (order.kind === 'move' && order.scout) return 'scouting';   // 69.2d
  return 'moving';
}

/** The order of precedence when a squad's members differ: the most pressing one speaks for the squad. */
const PRECEDENCE: FriendlyStatus[] = ['in contact', 'withdrawing', 'hurt', 'crewing', 'scouting', 'moving', 'holding', 'free', 'down'];
export function squadStatus(members: readonly FriendlyStatus[]): FriendlyStatus {
  for (const s of PRECEDENCE) if (members.includes(s)) return s;
  return 'free';
}

const norm = (v: Vec3): Vec3 => { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };

function pointName(state: GameState, id: number): string {
  return String.fromCharCode(65 + Math.max(0, (state.modeState.points?.sites ?? []).findIndex(s => s.id === id)));
}

/** An order in words, and the point it is about. */
export function describeOrder(state: GameState, order: SquadOrder | null, team: number): { text: string; target: SquadMarker['target'] } {
  if (!order) return { text: 'free', target: null };
  // 68.0: an order's place is a point or a position (core/orderForms.ts); a position reads as "here"
  const placed = (verb: string, o: Extract<SquadOrder, { kind: 'take' | 'hold' | 'bound' | 'flank' | 'move' | 'ride' }>) => {
    const place = orderPlace(state, o);
    return { text: `${verb} ${o.pointId !== undefined ? pointName(state, o.pointId) : 'here'}`, target: place ? { planetIndex: place.planetIndex, dir: place.surfaceDir } : null };
  };
  switch (order.kind) {
    case 'take': return placed('take', order);
    case 'hold': return placed('hold', order);
    case 'bound': return placed('bound to', order);
    case 'flank': return placed('flank', order);
    case 'move': return placed(order.scout ? 'scout' : 'go', order);   // 69.2d: a scout reads as one
    case 'ride': return placed('ride to', order);   // 68.18
    case 'engage': { const u = state.players.find(p => p.id === order.unitId); return { text: `engage ${u?.name ?? order.unitId}`, target: u ? { planetIndex: u.planetIndex, dir: u.surfaceDir } : null }; }
    case 'demolish': { const st = state.structures.find(x => x.id === order.structureId); return { text: `demolish ${st ? STRUCTURE_LABEL[st.type] ?? st.type : order.structureId}`, target: st ? { planetIndex: st.planetIndex, dir: st.surfaceDir } : null }; }   // 69.5d
    case 'crew': { const st = state.structures.find(x => x.id === order.structureId); return { text: `crew ${st?.type ?? 'seat'} ${order.structureId}`, target: st ? { planetIndex: st.planetIndex, dir: st.surfaceDir } : null }; }
    case 'escort': { const u = state.players.find(p => p.id === order.unitId); return { text: `escort ${u?.name ?? order.unitId}`, target: u ? { planetIndex: u.planetIndex, dir: u.surfaceDir } : null }; }
    case 'withdraw': { const exit = state.exits?.[team]; return { text: 'withdraw', target: exit ? { planetIndex: exit.planetIndex, dir: exit.facing } : null }; }
    case 'fireMission': return { text: 'fire mission', target: { planetIndex: order.planetIndex, dir: order.surfaceDir } };
    case 'free': return { text: 'free', target: null };
    case 'build': return { text: `build ${order.structure}`, target: { planetIndex: order.planetIndex, dir: order.surfaceDir } };   // 69.2d
    default: return { text: order.kind, target: null };
  }
}

function doingOf(state: GameState, p: PlayerState): string {
  if (!p.alive) return 'down';
  if (p.mount) { const st = state.structures.find(s => s.id === p.mount!.bodyId); if (st && drives(st)) return 'riding'; }   // 68.18: aboard a vehicle
  if (mountedStructure(p, state)) return 'manning a gun';
  if (p.airborne) return 'airborne';
  if (p.plantLeft !== undefined) return `planting ${p.planting ?? 'a beacon'}`;   // 69.2d: building, held still
  return p.stance && p.stance !== 'stand' ? p.stance : 'on foot';
}

/** 68.36: members further apart than this (person heights) stand in different clusters of their squad. */
export const SQUAD_CLUSTER_PEOPLE = 8;

/**
 * 68.36: a squad's members on its planet in clusters — single linkage at SQUAD_CLUSTER_PEOPLE (a member within that of any
 * in a cluster joins it) — each its centre and its count, the largest first (ties: the lowest member id first). Pure.
 */
export function squadClusters(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, members: readonly PlayerState[]): Array<{ dir: Vec3; n: number }> {
  if (!members.length) return [];
  const planet = state.planets[planetIndex];
  const link = planet ? (SQUAD_CLUSTER_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius : Infinity;   // (an angle)
  const ms = [...members].sort((a, b) => a.id - b.id), groupOf = ms.map((_, i) => i);
  const find = (i: number): number => (groupOf[i] === i ? i : (groupOf[i] = find(groupOf[i])));
  for (let i = 0; i < ms.length; i++) for (let j = i + 1; j < ms.length; j++) {
    const d = dacos(Math.max(-1, Math.min(1, ms[i].surfaceDir.x * ms[j].surfaceDir.x + ms[i].surfaceDir.y * ms[j].surfaceDir.y + ms[i].surfaceDir.z * ms[j].surfaceDir.z)));
    if (d <= link) groupOf[find(j)] = find(i);
  }
  const byRoot = new Map<number, PlayerState[]>();
  ms.forEach((p, i) => { const r = find(i); byRoot.set(r, [...(byRoot.get(r) ?? []), p]); });
  return [...byRoot.values()].sort((a, b) => b.length - a.length || a[0].id - b[0].id)
    .map(g => ({ dir: norm(g.reduce((a, p) => ({ x: a.x + p.surfaceDir.x, y: a.y + p.surfaceDir.y, z: a.z + p.surfaceDir.z }), { x: 0, y: 0, z: 0 })), n: g.length }));
}

/** The table for team `team` at `tick`; `contact` gives each unit's last shot and hit ticks (the match record's), `dt` the tick length. */
export function commandTable(state: GameState, team: number, tick: number, contact: { lastShot: Record<number, number>; lastHurt: Record<number, number> } = { lastShot: {}, lastHurt: {} }, dt = 1 / 120): CommandTable {
  const squads = (state.squads?.filter(s => s.team === team).length ? state.squads.filter(s => s.team === team) : squadsForTeam(state.players, team, 3, team * 1000));
  const markers: SquadMarker[] = squads.map((sq, k) => {
    const units = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p);
    const alive = units.filter(p => p.alive);
    const planetIndex = (alive[0] ?? units[0])?.planetIndex ?? 0;
    // 68.30: members aboard a hull are drawn with it; the block stands over those on foot (all of them when none is)
    const onFoot = alive.filter(p => p.planetIndex === planetIndex && !p.mount);
    const here = onFoot.length ? onFoot : alive.filter(p => p.planetIndex === planetIndex);
    // 68.36: the members on the ground in clusters (each within SQUAD_CLUSTER_PEOPLE of another in it); together, one at its centre
    const groups = squadClusters(state, planetIndex, here);
    const centre = groups.length ? groups[0].dir : null;
    const lead = alive[0];
    const order = lead && state.squads?.some(s => s.id === sq.id) ? squadOrderFor(state, lead.id, tick) : null;
    const d = describeOrder(state, order, team);
    const behind = !!lead && leftBehind(state, team, order, squadOrderSourceFor(state, lead.id, tick));   // 69.5f
    const members: MemberLine[] = units.map(p => {
      // 68.5: a member given its own order shows that order (and its state under it), not its squad's
      const mine = p.alive && state.squads?.some(s => s.id === sq.id) ? squadOrderFor(state, p.id, tick) : null;
      const direct = mine && JSON.stringify(mine) !== JSON.stringify(order) ? mine : null;
      return {
        id: p.id, name: p.name, alive: p.alive, health: p.health, doing: doingOf(state, p), status: friendlyStatus(state, p, direct ?? order, contact.lastShot[p.id], contact.lastHurt[p.id], tick, dt),
        planetIndex: p.planetIndex, dir: p.surfaceDir, mounted: !!p.mount, ...(direct ? { own: { glyph: orderGlyph(direct.kind), text: describeOrder(state, direct, team).text } } : {}), ...(p.role ? { role: p.role } : {}),
      };
    });
    return {
      id: sq.id, label: `${String.fromCharCode(65 + k)} squad`, team, planetIndex, centre, ...(groups.length > 1 ? { clusters: groups.slice(1) } : {}), alive: alive.length, total: units.length,
      order: (order?.kind === 'ride' ? `${d.text} · ${ridePhase(state, order, units)}` : !order && sq.released ? 'released' : 'scouted' in sq && sq.scouted ? `scouted · ${d.text}` : d.text) + (behind ? ' · left behind the front' : ''), glyph: orderGlyph(order?.kind ?? null), target: d.target,
      members,
      status: squadStatus(members.filter(m => m.alive).map(m => m.status)),
      ...(order?.kind === 'ride' ? { ride: ridePhase(state, order, units) } : {}),
      ...(aboardOne(state, alive) !== null ? { aboard: aboardOne(state, alive)! } : {}),
      ...(behind ? { leftBehind: true as const } : {}),
      ...((i => i ? { intent: { intent: i.intent, ...(i.since !== undefined ? { age: Math.max(0, (tick - i.since) * dt) } : {}) } } : {})(squadIntent(state, sq, tick))),   // X40
    };
  });
  const enemy = teamPicture(state, team, tick);
  const transports = transportMarkers(state, team, enemy);
  withAboard(transports, markers, state);
  return { squads: markers, enemy, pictureOn: !!state.teamTracks || !!state.sightings?.length, reserve: reserveView(state, team), transports, front: frontView(state, team), links: webLinks(state, team), plays: playViews(state, team), onFoot: vehiclesBarred(state, team), respawn: seatRespawn(state, team) };
}

/**
 * 63.7 (c): the buildings the commander's view and the orbit mark, since at person scale they no longer read by bulk —
 * the viewer's team's structures and the neutral sites (unowned supply, ruins); never the enemy's (the topo map's rule:
 * what the team knows of the enemy is its picture). `kind` names lane E's marker icon; `ruined` when destroyed.
 */
export interface BuildingMarker { id: string; kind: 'hq' | 'shieldDome' | 'mast' | 'pad' | 'radarTower' | 'gun' | 'crate' | 'beacon' | 'relay' | 'ruin'; label: string; team: number | null; planetIndex: number; dir: Vec3; ruined: boolean }

const STRUCTURE_KIND: Record<string, BuildingMarker['kind']> = {
  ecotourism: 'hq', shield: 'shieldDome', antenna: 'mast', landingPad: 'pad', tower: 'radarTower', emplacement: 'gun', supply: 'crate', beacon: 'beacon', relay: 'relay', fob: 'crate',   // 69.4: the FOB on the crate's plate until its own
};
export const STRUCTURE_LABEL: Record<string, string> = {
  ecotourism: 'HQ', shield: 'shield', house: 'building', antenna: 'antenna', landingPad: 'landing pad', tower: 'tower', emplacement: 'gun', supply: 'supply', beacon: 'beacon', relay: 'relay', fob: 'FOB',
};

export function buildingMarkers(state: Pick<GameState, 'players' | 'structures' | 'ruins'>, team: number): BuildingMarker[] {
  const teamOf = new Map(state.players.map(p => [p.id, p.teamIndex] as const));
  const out: BuildingMarker[] = [];
  for (const s of state.structures ?? []) {
    const kind = STRUCTURE_KIND[s.type];
    if (!kind) continue;   // a motorcycle is a vehicle, not a building
    const owner = s.supply?.teamIndex ?? (s.ownerId >= 0 ? teamOf.get(s.ownerId) ?? null : null);
    if (owner !== null && owner !== team) continue;   // the enemy's: only as the team knows it, which is its picture
    out.push({ id: `s${s.id}`, kind, label: STRUCTURE_LABEL[s.type] ?? s.type, team: owner, planetIndex: s.planetIndex, dir: s.surfaceDir, ruined: s.health <= 0 });
  }
  for (const r of state.ruins ?? []) {
    const rr = r as { ruinId?: number; id?: number; type?: string; planetIndex: number; surfaceDir: Vec3 };
    out.push({ id: `r${rr.ruinId ?? rr.id ?? out.length}`, kind: 'ruin', label: rr.type ? rr.type.replace(/([A-Z])/g, ' $1').toLowerCase() : 'ruin', team: null, planetIndex: rr.planetIndex, dir: rr.surfaceDir, ruined: true });
  }
  return out;
}

/** The squad nearest a screen point, within `radius` px (`project` gives a marker's screen position, null off-screen). */
/**
 * 68.35 (the owner's pick, A: "merge with zoom"): the table's squad blocks as drawn on screen, merged where they would land
 * on one another — a block within `w` × `h` px of a group's joins it (the group's place the mean of its members'); a
 * selected squad never merges (it keeps its own block, so a click on a merged block that selects its squads expands
 * them). Close in, nothing overlaps and every squad keeps its block, as before. Pure: screen points in, groups out (in the
 * squads' order, the selected first).
 */
export function mergeScreenBlocks(points: ReadonlyArray<{ id: number; x: number; y: number; selected: boolean }>, w = 64, h = 44): Array<{ ids: number[]; x: number; y: number }> {
  const groups: Array<{ ids: number[]; x: number; y: number; open: boolean }> = [];
  for (const q of [...points].sort((a, b) => Number(b.selected) - Number(a.selected))) {
    const g = q.selected ? undefined : groups.find(o => o.open && Math.abs(o.x - q.x) < w && Math.abs(o.y - q.y) < h);
    if (g) { const n = g.ids.length; g.x = (g.x * n + q.x) / (n + 1); g.y = (g.y * n + q.y) / (n + 1); g.ids.push(q.id); }
    else groups.push({ ids: [q.id], x: q.x, y: q.y, open: !q.selected });
  }
  return groups.map(({ ids, x, y }) => ({ ids, x, y }));
}

export function pickSquad(table: CommandTable, project: (m: SquadMarker) => { x: number; y: number } | null, x: number, y: number, radius = 24): number | null {
  let best: number | null = null; let bd = radius;
  for (const m of table.squads) {
    const p = project(m);
    if (!p) continue;
    const dx = p.x - x, dy = p.y - y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= bd) { bd = d; best = m.id; }
  }
  return best;
}

/**
 * 69.6d (decision 146): the front at the table — each sector of an Operations push as the team sees it (the attackers'
 * `taken` / `active` / `ahead`, the defenders' `lost` / `active` / `held`; `sector` alone without a push), each sector's
 * edge as a line across the front, and where to label it. Null without the sectors' layout. The sectors are the battle's
 * layout, known to both sides (what the objectives are is 69.2a's).
 */
export type SectorStatus = 'taken' | 'active' | 'ahead' | 'lost' | 'held' | 'ours' | 'theirs' | 'sector';
export interface FrontView {
  planetIndex: number;
  /** count + 1 edges from the attackers' side, each a line across the front; `bold` on the active sector's two. */
  edges: Array<{ points: Vec3[]; bold: boolean }>;
  sectors: Array<{ index: number; status: SectorStatus; label: string; at: Vec3 }>;
}
const SECTOR_WORDS: Record<SectorStatus, string> = { taken: 'taken', active: 'the front', ahead: 'ahead', lost: 'lost', held: 'held', ours: 'ours', theirs: 'theirs', sector: '' };
export function frontView(state: Pick<GameState, 'modeState'>, team: number): FrontView | null {
  const ps = state.modeState.points;
  const front = ps?.front;
  if (!front) return null;
  const push = ps.push;
  const sep = dacos(Math.max(-1, Math.min(1, front.a.x * front.b.x + front.a.y * front.b.y + front.a.z * front.b.z)));
  const half = Math.min(SECTOR_MAX_SIDE, 0.25 * sep);   // the edge lines' half-width across the front
  const across = (f: number) => Array.from({ length: 9 }, (_, i) => frontPoint(front, f, -half + (2 * half * i) / 8));
  // the front: the active sector's two edges (a push), or the live span's outer edges (69.6e, the tug of war)
  const live = push?.shape === 'tug' ? liveSectors(ps) : push && push.active >= 0 && push.active < front.count ? [push.active] : [];
  const lo = live.length ? live[0] : -1, hi = live.length ? live[live.length - 1] + 1 : -1;
  const edges = Array.from({ length: front.count + 1 }, (_, i) => ({ points: across(sectorEdge(front, i)), bold: live.length > 0 && (i === lo || i === hi) }));
  const statusOf = (k: number): SectorStatus => {
    if (!push) return 'sector';
    if (push.shape === 'tug' ? live.includes(k) : k === push.active) return 'active';
    if (push.shape === 'tug') {
      // 69.6e: a sector is whoever holds its objectives (the front moves both ways)
      const held = ps.sites.filter(s => s.sector === k);
      return held.length > 0 && held.every(s => s.owner === team) ? 'ours' : 'theirs';
    }
    if (team === push.defender) return k < push.active ? 'lost' : 'held';
    return k < push.active ? 'taken' : 'ahead';
  };
  const sectors = Array.from({ length: front.count }, (_, k) => {
    const status = statusOf(k);
    const last = k === front.count - 1;
    const ourBase = push && (push.shape === 'tug' && team === push.attacker ? k === 0 : last && team === push.defender);
    const theirBase = push && (push.shape === 'tug' && team === push.defender ? k === 0 : last && team !== push.defender);
    const name = 'sector ' + (k + 1) + (ourBase ? ' (our base)' : theirBase ? ' (their base)' : '');
    const words = SECTOR_WORDS[status];
    return { index: k, status, label: words ? name + ' · ' + words : name, at: frontPoint(front, (sectorEdge(front, k) + sectorEdge(front, k + 1)) / 2, -half * 0.9) };
  });
  return { planetIndex: front.planetIndex, edges, sectors };
}

/**
 * 68.22: a play at the table — the line the card reads ("Play 1 · support & assault — setting") and the two places the
 * overlay joins: the support's position and the objective. The team's plays still running.
 */
export interface PlayView { id: number; text: string; phase: Play['phase']; lifted: boolean; support: { planetIndex: number; dir: Vec3 }; objective: { planetIndex: number; dir: Vec3 } | null; squads: number[]; /** the objective's name, when a point */ at?: string; /** each squad's part in it */ roles: Record<number, 'support' | 'assault'> }
const PHASE_WORDS: Record<Play['phase'], string> = { setting: 'support moving up', assault: 'assault going in', done: 'done' };
export function playViews(state: GameState, team: number): PlayView[] {
  return (state.plays ?? []).filter(p => p.team === team && p.phase !== 'done').map(p => {
    const place = 'pointId' in p.objective && p.objective.pointId !== undefined ? orderPlace(state, { kind: 'hold', pointId: p.objective.pointId })
      : orderPlace(state, { kind: 'hold', planetIndex: (p.objective as { planetIndex: number }).planetIndex, surfaceDir: (p.objective as { surfaceDir: Vec3 }).surfaceDir });
    const at = 'pointId' in p.objective && p.objective.pointId !== undefined ? ` ${pointName(state, p.objective.pointId)}` : '';
    return {
      id: p.id, phase: p.phase, lifted: !!p.lifted, squads: [...p.support, ...p.assault],
      ...(at ? { at: at.trim() } : {}),
      roles: Object.fromEntries([...p.support.map(id => [id, 'support'] as const), ...p.assault.map(id => [id, 'assault'] as const)]),
      text: `Play ${p.id + 1}: support & assault${at} — ${p.lifted ? 'fire lifted' : PHASE_WORDS[p.phase]}`,
      support: { planetIndex: p.position.planetIndex, dir: p.position.surfaceDir },
      objective: place ? { planetIndex: place.planetIndex, dir: place.surfaceDir } : null,
    };
  });
}
